import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Save, Bookmark, Trash2, Pencil } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const BOOK_INFO_FIELDS = [
  ["title",          "Title",           "text"],
  ["subtitle",       "Subtitle",        "text"],
  ["author",         "Author",          "text"],
  ["publisher",      "Publisher",       "text"],
  ["imprint",        "Imprint",         "text"],
  ["copyright_year", "Copyright Year",  "text"],
  ["email",          "Email",           "email"],
  ["isbn",           "ISBN (optional)", "text"],
];

// -----------------------------------------------------------------------------
// BookInfoPresetControls — save/load a snapshot of Book Info fields.
// -----------------------------------------------------------------------------
export function BookInfoPresetControls({ presets, currentInfo, onSave, onLoad, onDelete }) {
  const [showSave, setShowSave] = useState(false);
  const [showLoad, setShowLoad] = useState(false);
  const [name, setName] = useState("");
  const [pick, setPick] = useState(() => {
    const initial = {};
    for (const [k] of BOOK_INFO_FIELDS) initial[k] = !!(currentInfo[k] || "").trim();
    return initial;
  });

  const setPickAt = (k, v) => setPick((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    const fields = {};
    for (const [k] of BOOK_INFO_FIELDS) {
      if (pick[k] && (currentInfo[k] || "").toString().trim()) fields[k] = currentInfo[k];
    }
    const saved = await onSave(fields, name);
    if (saved) { setName(""); setShowSave(false); }
  };

  return (
    <div className="flex items-center gap-2" data-testid="mc-info-preset-controls">
      {presets.length > 0 && (
        <div className="relative">
          <button type="button" onClick={()=>{ setShowLoad(!showLoad); setShowSave(false); }}
            className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-[color:var(--border)] hover:bg-[color:var(--canvas)]"
            data-testid="mc-info-load-btn">
            <Bookmark className="w-3 h-3"/> Load ({presets.length})
          </button>
          {showLoad && (
            <div className="absolute right-0 top-full mt-1 z-30 min-w-[280px] bg-white border border-[color:var(--border)] shadow-lg" data-testid="mc-info-load-menu">
              <div className="p-1">
                <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] px-2 py-1">Book-info presets</p>
                {presets.map((p) => {
                  const preview = Object.entries(p.fields || {}).slice(0,3).map(([k,v]) => `${k}: ${v}`).join(" · ");
                  return (
                    <div key={p.id} className="group flex items-center justify-between px-2 py-1 hover:bg-[color:var(--canvas)]" data-testid={`mc-info-preset-${p.id}`}>
                      <button type="button" onClick={()=>{ onLoad(p); setShowLoad(false); }}
                        className="text-left flex-1 text-xs min-w-0" data-testid={`mc-info-preset-${p.id}-pick`}>
                        <div className="truncate font-medium">{p.name}</div>
                        <div className="truncate text-[color:var(--text-muted)]">{preview || "(empty)"}</div>
                      </button>
                      <button type="button" onClick={()=>onDelete(p.id)}
                        className="p-1 opacity-60 group-hover:opacity-100 text-[color:var(--text-muted)] hover:text-red-600"
                        title="Delete preset" data-testid={`mc-info-preset-${p.id}-delete`}>
                        <Trash2 className="w-3 h-3"/>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      {!showSave ? (
        <button type="button" onClick={()=>setShowSave(true)}
          className="text-xs inline-flex items-center gap-1 text-[color:var(--primary)] hover:underline"
          data-testid="mc-info-save-btn">
          <Save className="w-3 h-3"/> Save as preset
        </button>
      ) : (
        <div className="absolute right-6 mt-8 z-30 w-[340px] bg-white border border-[color:var(--border)] shadow-lg p-3"
             data-testid="mc-info-save-form">
          <p className="text-xs font-medium mb-2">Save book-info preset</p>
          <input value={name} onChange={(e)=>setName(e.target.value)}
            placeholder="Preset name (e.g. PublishingHub360 defaults)"
            className="w-full border border-[color:var(--border)] px-2 py-1 text-xs mb-2 focus:outline-none focus:border-[color:var(--primary)]"
            data-testid="mc-info-save-name"/>
          <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Include fields</p>
          <div className="grid grid-cols-2 gap-1 mb-3">
            {BOOK_INFO_FIELDS.map(([k, label]) => (
              <label key={k} className="text-xs inline-flex items-center gap-1">
                <input type="checkbox" checked={!!pick[k]} onChange={(e)=>setPickAt(k, e.target.checked)}
                  data-testid={`mc-info-save-pick-${k}`}/>
                <span className="truncate">{label}</span>
                {!(currentInfo[k] || "").toString().trim() && <span className="text-[9px] text-[color:var(--text-muted)]">(empty)</span>}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={()=>setShowSave(false)} className="text-xs px-2 py-1 border border-[color:var(--border)]"
              data-testid="mc-info-save-cancel">Cancel</button>
            <button type="button" onClick={handleSave} className="text-xs px-2 py-1 bg-[color:var(--primary)] text-white"
              data-testid="mc-info-save-confirm">Save preset</button>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// PublishersKitBar — bundle several presets and apply with one click.
// -----------------------------------------------------------------------------
export function PublishersKitBar({ kits, presets, team, workspaces, currentTocNote, currentIntroduction,
                                   onApplyKit, onDeleteKit, onCreateKit, onUpdateKit,
                                   onResetKit }) {
  const [showModal, setShowModal] = useState(false);
  const [editingKit, setEditingKit] = useState(null);
  const front = presets.filter((p) => p.kind === "front");
  const back  = presets.filter((p) => p.kind === "back");
  const infoP = presets.filter((p) => p.kind === "info");

  const openCreate = () => { setEditingKit(null); setShowModal(true); };
  const openEdit = (kit) => { setEditingKit(kit); setShowModal(true); };

  const handleSubmit = async (payload) => {
    const result = editingKit ? await onUpdateKit(editingKit.id, payload) : await onCreateKit(payload);
    if (result) setShowModal(false);
  };

  return (
    <div className="border border-[color:var(--border)] bg-[color:var(--canvas)]/60 p-4" data-testid="mc-kit-bar">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">§ Publisher's Kit</p>
          <p className="text-xs text-[color:var(--text-muted)] mt-1">
            Bundle several presets and apply the whole set to any new book with one click.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {kits.length > 0 && <ApplyKitDropdown kits={kits} onApplyKit={onApplyKit} onDeleteKit={onDeleteKit} onEditKit={openEdit}/>}
          <button type="button" onClick={openCreate}
            className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-[color:var(--primary)] bg-white text-[color:var(--primary)] hover:bg-[color:var(--canvas)]"
            data-testid="mc-kit-create-btn">
            + New kit
          </button>
          {onResetKit && (
            <button type="button"
              onClick={async () => {
                if (!window.confirm("Reset Publisher's Kit fields (front / back matter, TOC note, introduction) to their empty defaults for this book? Your chapters and cover are not affected."))
                  return;
                await onResetKit();
              }}
              className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-[color:var(--border)] bg-white text-[color:var(--text-muted)] hover:text-red-700 hover:border-red-500"
              data-testid="mc-kit-reset-btn"
              title="Revert all Publisher's Kit fields to defaults">
              Reset kit
            </button>
          )}
        </div>
      </div>
      {showModal && (
        <CreateKitModal
          onClose={()=>setShowModal(false)} onSubmit={handleSubmit}
          frontPresets={front} backPresets={back} infoPresets={infoP}
          editingKit={editingKit} team={team} workspaces={workspaces}
          currentTocNote={currentTocNote} currentIntroduction={currentIntroduction}
        />
      )}
    </div>
  );
}

function _kitPreviewBits(k) {
  return [
    k.info_preset_id ? "book info" : null,
    (k.front_preset_ids?.length || 0) > 0 ? `${k.front_preset_ids.length} front` : null,
    (k.back_preset_ids?.length || 0) > 0 ? `${k.back_preset_ids.length} back` : null,
    (k.toc_note || "").trim() ? "TOC note" : null,
    (k.introduction || "").trim() ? "intro" : null,
  ].filter(Boolean);
}

function ApplyKitDropdown({ kits, onApplyKit, onDeleteKit, onEditKit }) {
  const [open, setOpen] = useState(false);
  const handleApply = (id) => { setOpen(false); onApplyKit(id); };
  const handleEdit  = (k)  => { setOpen(false); onEditKit(k); };
  const handleDel   = (id) => { setOpen(false); onDeleteKit(id); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
          className="cursor-pointer text-xs inline-flex items-center gap-1 px-2 py-1 border border-[color:var(--border)] bg-white hover:bg-[color:var(--canvas)]"
          data-testid="mc-kit-apply-toggle">
          <Bookmark className="w-3 h-3"/> Apply kit ({kits.length})
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={4}
        className="w-[380px] p-1 bg-white border border-[color:var(--border)] shadow-lg z-50"
        data-testid="mc-kit-apply-menu">
        {kits.map((k) => (
          <div key={k.id} className="group flex items-center gap-1 px-2 py-1.5 hover:bg-[color:var(--canvas)]" data-testid={`mc-kit-row-${k.id}`}>
            <button type="button" onClick={()=>handleApply(k.id)}
              className="text-left flex-1 text-xs min-w-0" data-testid={`mc-kit-row-${k.id}-apply`}>
              <div className="truncate font-medium text-[color:var(--text)] inline-flex items-center gap-2">
                {k.name}
                {k.is_shared && (
                  <span className="text-[9px] font-normal uppercase tracking-widest px-1.5 py-0.5 bg-emerald-100 text-emerald-800" data-testid={`mc-kit-row-${k.id}-shared-badge`}>
                    Shared
                  </span>
                )}
                {!k.is_mine && (
                  <span className="text-[9px] font-normal uppercase tracking-widest px-1.5 py-0.5 bg-slate-100 text-slate-600">Read-only</span>
                )}
              </div>
              <div className="truncate text-[color:var(--text-muted)]">{_kitPreviewBits(k).join(" · ") || "empty"}</div>
            </button>
            {k.is_mine && (
              <>
                <button type="button" onClick={()=>handleEdit(k)}
                  className="p-1 opacity-60 hover:opacity-100 text-[color:var(--text-muted)] hover:text-[color:var(--primary)]"
                  title="Edit kit" data-testid={`mc-kit-row-${k.id}-edit`}>
                  <Pencil className="w-3 h-3"/>
                </button>
                <button type="button"
                  onClick={()=>handleDel(k.id)}
                  className="p-1 opacity-60 hover:opacity-100 text-[color:var(--text-muted)] hover:text-red-600"
                  title="Delete kit" data-testid={`mc-kit-row-${k.id}-delete`}>
                  <Trash2 className="w-3 h-3"/>
                </button>
              </>
            )}
          </div>
        ))}
        {kits.length === 0 && (
          <div className="px-3 py-2 text-xs text-[color:var(--text-muted)]">No saved kits yet.</div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// -----------------------------------------------------------------------------
// CreateKitModal — create OR edit a Publisher's Kit.
// iter-99 — Sharing scope is now a single dropdown covering "Private",
// the caller's ph360 team, and every workspace they own or belong to.
// -----------------------------------------------------------------------------
export function CreateKitModal({ onClose, onSubmit, frontPresets, backPresets, infoPresets,
                         editingKit, team, workspaces, currentTocNote, currentIntroduction }) {
  const isEdit = !!editingKit;
  const [name, setName] = useState(editingKit?.name || "");
  const [description, setDescription] = useState(editingKit?.description || "");
  const [frontIds, setFrontIds] = useState(() => new Set(editingKit?.front_preset_ids || []));
  const [backIds, setBackIds] = useState(() => new Set(editingKit?.back_preset_ids || []));
  const [infoId, setInfoId] = useState(editingKit?.info_preset_id || "");
  const [tocNote, setTocNote] = useState(editingKit?.toc_note || "");
  const [introduction, setIntroduction] = useState(editingKit?.introduction || "");
  // iter-99/101 — sharing scope is now TWO controls:
  //   team scope (single choice — either off or the caller's ph360 team)
  //   workspace scope (multi-select — check as many workspaces as needed)
  // Old kits (pre-iter-101) only carried the singular `workspace_id`;
  // lift it into the plural list so the checkbox pre-selects it.
  const [teamOn, setTeamOn] = useState(() => !!editingKit?.team_id);
  const [workspaceIds, setWorkspaceIds] = useState(() => {
    const list = editingKit?.workspace_ids && editingKit.workspace_ids.length
      ? editingKit.workspace_ids
      : (editingKit?.workspace_id ? [editingKit.workspace_id] : []);
    return new Set(list);
  });

  // Defensive: if the caller lost access to a previously-shared
  // workspace, drop it from the pre-selection so we don't POST a
  // broken id back.
  useEffect(() => {
    const validIds = new Set((workspaces || []).map((w) => w.id));
    setWorkspaceIds((prev) => {
      const next = new Set();
      for (const id of prev) if (validIds.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces]);
  useEffect(() => {
    if (teamOn && (!team || editingKit?.team_id !== team.id)) {
      // caller doesn't have a team any more (or it changed) — clear
      if (!team) setTeamOn(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleWorkspace = (wid) => {
    setWorkspaceIds((prev) => {
      const next = new Set(prev);
      if (next.has(wid)) next.delete(wid); else next.add(wid);
      return next;
    });
  };

  const toggle = (set, setter, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Kit name is required"); return; }
    if (frontIds.size === 0 && backIds.size === 0 && !infoId
        && !tocNote.trim() && !introduction.trim()) {
      toast.error("Pick at least one preset or add TOC note / introduction");
      return;
    }
    // iter-101 — translate the scope controls into API fields.
    // team_id: null unless the caller ticked the team box.
    // workspace_ids: [] when nothing ticked (unshares).
    const payload = {
      name: name.trim(), description: description.trim(),
      front_preset_ids: Array.from(frontIds),
      back_preset_ids: Array.from(backIds),
      info_preset_id: infoId || null,
      toc_note: tocNote, introduction,
      team_id: teamOn && team?.id ? team.id : null,
      workspace_ids: Array.from(workspaceIds),
    };
    await onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" data-testid="mc-kit-create-modal"
      onClick={(e)=>{ if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white border border-[color:var(--border)] p-5 w-[620px] max-w-[94vw] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium">{isEdit ? "Edit Publisher's Kit" : "Create Publisher's Kit"}</h3>
          <button onClick={onClose} className="btn-ghost" data-testid="mc-kit-modal-x"><X className="w-4 h-4"/></button>
        </div>
        <p className="text-xs text-[color:var(--text-muted)] mb-4">
          {isEdit ? "Update the kit's composition. Applying it later will use these changes." :
                    "Pick which of your saved presets should be applied together when this kit is loaded."}
        </p>
        <label className="block mb-2">
          <span className="block text-xs uppercase tracking-widest mb-1">Kit name</span>
          <input value={name} onChange={(e)=>setName(e.target.value)}
            placeholder="e.g. PublishingHub360 default kit"
            className="w-full border border-[color:var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--primary)]"
            data-testid="mc-kit-create-name"/>
        </label>
        <label className="block mb-4">
          <span className="block text-xs uppercase tracking-widest mb-1">Description (optional)</span>
          <input value={description} onChange={(e)=>setDescription(e.target.value)}
            className="w-full border border-[color:var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--primary)]"
            data-testid="mc-kit-create-desc"/>
        </label>

        {/* iter-101 — Share with… (Team + multi-select Workspaces).
            The team is optional and mutually a checkbox (you either
            share with the team or you don't). Workspaces are
            individually selectable — pick as many as you like, and
            every member of every ticked workspace can APPLY the kit. */}
        <div className="mb-4 border border-[color:var(--border)] bg-[color:var(--canvas)]/50 p-3 space-y-3">
          <p className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">Share with…</p>

          {team && (
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={teamOn}
                     onChange={(e)=>setTeamOn(e.target.checked)}
                     className="mt-0.5" data-testid="mc-kit-share-team"/>
              <div className="min-w-0">
                <p className="text-xs font-medium">
                  Team · <span className="text-[color:var(--text-muted)]">{team.name}</span>
                </p>
                <p className="text-[10px] text-[color:var(--text-muted)] mt-0.5">
                  Every teammate can APPLY this kit to their books.
                </p>
              </div>
            </label>
          )}

          <div>
            <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">
              Workspaces <span className="normal-case tracking-normal text-[color:var(--text-muted)]/80">— tick every workspace the kit should appear in</span>
            </p>
            {(workspaces || []).length === 0 ? (
              <p className="text-[10px] text-[color:var(--text-muted)]" data-testid="mc-kit-share-scope-empty">
                You aren't in any workspace yet — invite a VA / editor to a
                workspace on the Library → Workspaces page to see them here.
              </p>
            ) : (
              <div className="max-h-40 overflow-auto border border-[color:var(--border)] bg-white divide-y divide-[color:var(--border)]"
                   data-testid="mc-kit-share-workspaces">
                {workspaces.map((w) => {
                  const checked = workspaceIds.has(w.id);
                  return (
                    <label key={w.id}
                           className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-[color:var(--canvas)]"
                           data-testid={`mc-kit-share-ws-${w.id}`}>
                      <input type="checkbox" checked={checked}
                             onChange={()=>toggleWorkspace(w.id)}
                             data-testid={`mc-kit-share-ws-${w.id}-cb`}/>
                      <span className="text-xs truncate">{w.name || "Workspace"}</span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-[color:var(--text-muted)] mt-1">
              {workspaceIds.size === 0 && !teamOn
                ? "Private — only you can apply this kit."
                : workspaceIds.size === 0
                  ? "Shared with your team."
                  : `Shared with ${workspaceIds.size} workspace${workspaceIds.size === 1 ? "" : "s"}${teamOn ? " + team" : ""}. Only you can edit or delete it.`}
            </p>
          </div>
        </div>

        <p className="text-xs uppercase tracking-widest mb-2">Book-info preset (pick one)</p>
        <div className="space-y-1 mb-4 border border-[color:var(--border)] max-h-[110px] overflow-y-auto">
          <label className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer hover:bg-[color:var(--canvas)]">
            <input type="radio" name="info-preset" checked={infoId===""} onChange={()=>setInfoId("")} data-testid="mc-kit-info-none"/>
            <span className="text-[color:var(--text-muted)]">— none —</span>
          </label>
          {infoPresets.map((p) => (
            <label key={p.id} className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer hover:bg-[color:var(--canvas)]" data-testid={`mc-kit-info-${p.id}`}>
              <input type="radio" name="info-preset" checked={infoId===p.id} onChange={()=>setInfoId(p.id)}/>
              <span className="truncate">{p.name}</span>
            </label>
          ))}
          {infoPresets.length === 0 && <p className="text-xs text-[color:var(--text-muted)] px-2 py-1">No book-info presets saved yet.</p>}
        </div>

        <KitPresetList label="Front-matter presets" items={frontPresets} selectedIds={frontIds}
          onToggle={(id)=>toggle(frontIds, setFrontIds, id)} testIdPrefix="mc-kit-front"/>
        <KitPresetList label="Back-matter presets" items={backPresets} selectedIds={backIds}
          onToggle={(id)=>toggle(backIds, setBackIds, id)} testIdPrefix="mc-kit-back"/>

        <KitLongText label="TOC note" value={tocNote} setValue={setTocNote}
          placeholder="Optional editorial line printed above the Table of Contents."
          rows={2} current={currentTocNote}
          testId="mc-kit-toc-note" fromCurrentTestId="mc-kit-toc-note-fromcurrent"/>
        <KitLongText label="Introduction" value={introduction} setValue={setIntroduction}
          placeholder="Boilerplate opening the reader sees on the Introduction page (optional)."
          rows={4} current={currentIntroduction} className="font-reading"
          testId="mc-kit-introduction" fromCurrentTestId="mc-kit-introduction-fromcurrent"/>

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-ghost text-sm" onClick={onClose} data-testid="mc-kit-create-cancel">Cancel</button>
          <button type="button" className="btn-primary text-sm" onClick={submit} data-testid="mc-kit-create-submit">
            {isEdit ? "Update kit" : "Save kit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function KitPresetList({ label, items, selectedIds, onToggle, testIdPrefix }) {
  return (
    <>
      <p className="text-xs uppercase tracking-widest mb-2 mt-2">{label} ({items.length})</p>
      <div className="space-y-1 mb-3 border border-[color:var(--border)] max-h-[130px] overflow-y-auto">
        {items.length === 0 && <p className="text-xs text-[color:var(--text-muted)] px-2 py-1">No {label.toLowerCase()} saved yet.</p>}
        {items.map((p) => (
          <label key={p.id} className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer hover:bg-[color:var(--canvas)]" data-testid={`${testIdPrefix}-${p.id}`}>
            <input type="checkbox" checked={selectedIds.has(p.id)} onChange={()=>onToggle(p.id)}/>
            <span className="truncate flex-1"><span className="font-medium">{p.name}</span> <span className="text-[color:var(--text-muted)]">— {p.title}</span></span>
          </label>
        ))}
      </div>
    </>
  );
}

// Shared textarea + "⇦ Use current" button used for both TOC note and Introduction.
function KitLongText({ label, value, setValue, placeholder, rows, current, className, testId, fromCurrentTestId }) {
  return (
    <div className={`mb-3 ${label === "TOC note" ? "mt-3 border-t border-[color:var(--border)] pt-3" : ""}`}>
      <p className="text-xs uppercase tracking-widest mb-2">{label}</p>
      <div className="flex items-start gap-2 mb-1">
        <textarea rows={rows} value={value} onChange={(e)=>setValue(e.target.value)}
          placeholder={placeholder}
          className={`flex-1 border border-[color:var(--border)] px-2 py-1 text-xs focus:outline-none focus:border-[color:var(--primary)] ${className || ""}`}
          data-testid={testId}/>
        {current?.trim() && (
          <button type="button" onClick={()=>setValue(current)}
            className="text-xs px-2 py-1 border border-[color:var(--border)] whitespace-nowrap self-start"
            title={`Copy the ${label} from the current book`}
            data-testid={fromCurrentTestId}>
            ⇦ Use current
          </button>
        )}
      </div>
      <p className="text-[10px] text-[color:var(--text-muted)]">Leave empty to leave the target book's {label} untouched.</p>
    </div>
  );
}
